import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useModal } from "../../context/ModalContext";
import { useNavigate, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField as MuiTextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { get_country, get_state, get_city } from "../../api/ControllApi";
import useCachedApi from "../../hooks/useCachedApi";
import { get_page } from "../../api/PageApi";
import { useSettings } from "../../context/Settings";
import { assetUrl } from "../../utils/baseUrls";

// ─── Debounce hook (outside component — same as Account.jsx) ─────────────────
function useDebounce(value, delay = 400) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}
// ─────────────────────────────────────────────────────────────────────────────

export default function SignupModal() {
  const { showSignup, setShowSignup } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const { requestApi } = useCachedApi();
  const { settings } = useSettings();

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [referralReadOnly, setReferralReadOnly] = useState(false);

  // ── Location search state ─────────────────────────────────────────────────
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const debouncedCountry = useDebounce(countrySearch, 400);
  const debouncedState = useDebounce(stateSearch, 400);
  const debouncedCity = useDebounce(citySearch, 400);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // ── Policy modal state ────────────────────────────────────────────────────
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyContent, setPolicyContent] = useState({
    title: "",
    content: "",
    loading: false,
  });
  const [signupTerms, setSignupTerms] = useState("");
  const [loadingSignupTerms, setLoadingSignupTerms] = useState(false);

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: localStorage.getItem("phone") || "",
    email: "",
    gender: "",
    dob: "",
    address: "",
    pin_code: "",
    image: null,
    country_id: "",
    state_id: "",
    city_id: "",
    referral_code: "",
  });

  // Max DOB: user must be 18+
  const maxDob = new Date();
  maxDob.setFullYear(maxDob.getFullYear() - 18);
  const maxDobString = maxDob.toISOString().split("T")[0];

  // ── On modal open: sync phone & check referral code ───────────────────────
  useEffect(() => {
    if (showSignup) {
      setForm((prev) => ({
        ...prev,
        phone: localStorage.getItem("phone") || "",
      }));
      const params = new URLSearchParams(location.search);
      const ref = params.get("ref");
      if (ref) {
        setForm((prev) => ({ ...prev, referral_code: ref }));
        setReferralReadOnly(true);
      }
    }
  }, [showSignup, location.search]);

  useEffect(() => {
    if (showSignup) {
      const fetchSignupTerms = async () => {
        setLoadingSignupTerms(true);
        try {
          const res = await get_page("signup_terms_and_condition");
          if (res?.success && res?.data?.content) {
            setSignupTerms(res.data.content);
          }
        } catch (err) {

        } finally {
          setLoadingSignupTerms(false);
        }
      };
      fetchSignupTerms();
    }
  }, [showSignup]);

  // ── Fetch countries on debounced search change ────────────────────────────
  useEffect(() => {
    if (!showSignup) return;
    requestApi(`countries-${debouncedCountry}`, () => get_country({ search: debouncedCountry }))
      .then((res) => setCountries(res?.data || []))
      .catch(() => {});
  }, [debouncedCountry, requestApi, showSignup]);

  // ── Fetch states when country or debounced search changes ─────────────────
  useEffect(() => {
    if (!showSignup) return;
    if (form.country_id) {
      requestApi(`states-${form.country_id}-${debouncedState}`, () =>
        get_state({ country_id: form.country_id, search: debouncedState })
      )
        .then((res) => setStates(res?.data || []))
        .catch(() => {});
    } else {
      setStates([]);
    }
  }, [form.country_id, debouncedState, requestApi, showSignup]);

  // ── Fetch cities when state or debounced search changes ───────────────────
  useEffect(() => {
    if (!showSignup) return;
    if (form.state_id) {
      requestApi(`cities-${form.state_id}-${debouncedCity}`, () =>
        get_city({ state_id: form.state_id, search: debouncedCity })
      )
        .then((res) => setCities(res?.data || []))
        .catch(() => {});
    } else {
      setCities([]);
    }
  }, [form.state_id, debouncedCity, requestApi, showSignup]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClose = () => setShowSignup(false);

  // Real-time validation on change — mirrored from Account.jsx handleChange
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (name === "email") {
        if (!value) {
          newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = "Enter a valid email address";
        } else {
          delete newErrors.email;
        }
      } else if (["first_name", "last_name", "address"].includes(name)) {
        if (!value.trim()) {
          newErrors[name] = `${name.replace("_", " ")} is required`;
        } else {
          delete newErrors[name];
        }
      } else if (name === "pin_code") {
        if (!value) {
          newErrors.pin_code = "Pin code is required";
        } else if (value.length !== 6) {
          newErrors.pin_code = "Pin code must be 6 digits";
        } else {
          delete newErrors.pin_code;
        }
      } else if (name === "gender") {
        if (!value) {
          newErrors.gender = "Please select gender";
        } else {
          delete newErrors.gender;
        }
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  // Image selection — same validation as Account.jsx handleImageChange
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Only image files allowed (jpg, jpeg, png, jfif)");
      return;
    }
    setForm((prev) => ({ ...prev, image: file }));
    setErrors((prev) => {
      const newErrs = { ...prev };
      delete newErrs.image;
      return newErrs;
    });
  };

  const openPolicyModal = async (slug, title) => {
    setShowPolicyModal(true);
    setPolicyContent({ title, content: "", loading: true });
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}pages/slug/${slug}`
      );
      if (response.data?.status && response.data?.data) {
        setPolicyContent({ title, content: response.data.data.content, loading: false });
      } else {
        setPolicyContent({ title, content: "<p>Content not found.</p>", loading: false });
      }
    } catch {
      setPolicyContent({ title, content: "<p>Failed to load content.</p>", loading: false });
    }
  };

  // ── Full validation before submit — mirrored from Account.jsx validateProfile
  const validateForm = () => {
    const newErrors = {};

    if (!form.image) newErrors.image = "Profile image is required";
    if (!form.first_name.trim()) newErrors.first_name = "First name is required";
    if (!form.last_name.trim()) newErrors.last_name = "Last name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!form.gender) newErrors.gender = "Please select gender";

    if (!form.dob) {
      newErrors.dob = "Date of birth is required";
    } else {
      const dobDate = new Date(form.dob);
      const today = new Date();
      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) age--;
      if (age < 18) newErrors.dob = "You must be at least 18 years old";
    }

    if (!form.country_id) newErrors.country_id = "Country is required";
    if (!form.state_id) newErrors.state_id = "State is required";
    if (!form.city_id) newErrors.city_id = "City is required";
    if (!form.address.trim()) newErrors.address = "Address is required";

    if (!form.pin_code) {
      newErrors.pin_code = "Pin code is required";
    } else if (form.pin_code.length !== 6) {
      newErrors.pin_code = "Pin code must be 6 digits";
    }

    setErrors(newErrors);
    return newErrors;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!agree) {
      toast.error("Please agree to Terms and Conditions");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Show only first error as toast (same as Account.jsx)
      const firstMsg = Object.values(validationErrors)[0];
      toast.error(firstMsg, { autoClose: 3000 });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === "image") {
          if (form.image instanceof File) formData.append("image", form.image);
        } else {
          formData.append(
            key,
            typeof form[key] === "string" ? form[key].trim() : form[key]
          );
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}register`,
        formData
      );

      if (response.data.status === 200) {
        toast.success("Registration successful");
        localStorage.setItem("authToken", response.data.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
        navigate("/");
        handleClose();
        window.location.reload();
      }
    } catch (error) {
      const errResponse = error.response?.data;
      if (errResponse?.status === 422 && errResponse.errors) {
        const firstField = Object.keys(errResponse.errors)[0];
        const firstErrMsg = errResponse.errors[firstField][0];
        toast.error(firstErrMsg, { autoClose: 3000 });
        const apiErrors = {};
        Object.keys(errResponse.errors).forEach((key) => {
          apiErrors[key] = errResponse.errors[key][0];
        });
        setErrors(apiErrors);
      } else {
        toast.error(errResponse?.message || "Something went wrong", { autoClose: 3000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Field definitions ─────────────────────────────────────────────────────
  const fields = [
    { label: "First Name", name: "first_name", required: true },
    { label: "Last Name", name: "last_name", required: true },
    { label: "Email", name: "email", type: "email", required: true },
    { label: "Gender", name: "gender", type: "radio", required: true },
    { label: "Phone", name: "phone", readOnly: true, required: true },
    { label: "Date of Birth", name: "dob", type: "date", required: true },
    {
      label: "Country",
      name: "country_id",
      type: "autocomplete",
      required: true,
      options: countries,
      getLabel: (o) => o.country_name || "",
    },
    {
      label: "State",
      name: "state_id",
      type: "autocomplete",
      required: true,
      options: states,
      getLabel: (o) => o.state_name || "",
      disabled: !form.country_id,
    },
    {
      label: "City",
      name: "city_id",
      type: "autocomplete",
      required: true,
      options: cities,
      getLabel: (o) => o.city_name || "",
      disabled: !form.state_id,
    },
    { label: "Address", name: "address", required: true },
    { label: "Pin Code", name: "pin_code", required: true },
    {
      label: "Referral Code",
      name: "referral_code",
      readOnly: referralReadOnly,
      required: false,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        show={showSignup}
        onHide={handleClose}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
        style={{ zIndex: 2000, marginTop: "40px" }}
      >
        {/* ── Header ── */}
        <Modal.Header closeButton style={{ padding: "12px", border: "none" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 2 },
                width: "100%",
                px: 1,
              }}
            >
              <Box
                component="img"
                src={settings?.data?.logo ? assetUrl(settings.data.logo) : "/assets/images/logo.png"}
                alt="logo"
                sx={{
                  height: { xs: 42, sm: 55, md: 65 },
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                Create a Geo Rental Farms Account
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: 90, sm: 120, md: 140 },
                height: 4,
                bgcolor: "#608900",
                borderRadius: 2,
              }}
            />
          </Box>
        </Modal.Header>

        {/* ── Body ── */}
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>

          {/* Profile Image — same edit-icon pattern as Account.jsx */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={() => fileInputRef.current.click()}
            >
              <img
                src={
                  form.image
                    ? URL.createObjectURL(form.image)
                    : "/assets/images/avatar.png"
                }
                alt="profile"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  border: "4px solid white",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                  objectFit: "cover",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "#FEB821",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid white",
                }}
              >
                <EditIcon style={{ width: 16, height: 16, color: "#fff" }} />
              </Box>
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              hidden
              accept=".png,.jpeg,.jpg,.jfif"
              onChange={handleImageSelect}
            />
          </Box>

          {/* Image error */}
          {errors.image && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography sx={{ color: "red", fontSize: 12 }}>
                {errors.image}
              </Typography>
            </Box>
          )}

          {/* ── Form ── */}
          <Form>
            <Row>
              {fields.map((f) => (
                <Col xs={12} sm={12} md={6} key={f.name}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600 }}>
                      {f.label}{" "}
                      {f.required !== false && (
                        <span style={{ color: "red" }}>*</span>
                      )}
                    </Form.Label>

                    {/* Gender radios */}
                    {f.type === "radio" ? (
                      <>
                        <div>
                          {[
                            { label: "Male", value: "1" },
                            { label: "Female", value: "2" },
                            { label: "Other", value: "3" },
                          ].map((opt) => (
                            <Form.Check
                              key={opt.value}
                              inline
                              label={opt.label}
                              type="radio"
                              name="gender"
                              value={opt.value}
                              checked={form.gender === opt.value}
                              onChange={handleChange}
                            />
                          ))}
                        </div>
                        {errors.gender && (
                          <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                            {errors.gender}
                          </div>
                        )}
                      </>
                    ) : f.type === "autocomplete" ? (
                      /* Country / State / City autocomplete — same as Account.jsx */
                      <Autocomplete
                        options={f.options || []}
                        getOptionLabel={f.getLabel}
                        openOnFocus
                        autoHighlight
                        filterOptions={(x) => x}
                        forcePopupIcon={true}
                        disabled={f.disabled || false}
                        slotProps={{
                          popper: {
                            sx: {
                              zIndex: 3000,
                            },
                          },
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={
                          f.options?.find((o) => o.id === form[f.name]) || null
                        }
                        onChange={(event, newValue) => {
                          setForm((prev) => {
                            const updated = {
                              ...prev,
                              [f.name]: newValue ? newValue.id : "",
                            };
                            if (f.name === "country_id") {
                              updated.state_id = "";
                              updated.city_id = "";
                            } else if (f.name === "state_id") {
                              updated.city_id = "";
                            }
                            return updated;
                          });
                          setErrors((prev) => {
                            const newErrs = { ...prev };
                            delete newErrs[f.name];
                            if (f.name === "country_id") {
                              delete newErrs.state_id;
                              delete newErrs.city_id;
                            } else if (f.name === "state_id") {
                              delete newErrs.city_id;
                            }
                            return newErrs;
                          });
                        }}
                        onInputChange={(event, newInputValue) => {
                          if (f.name === "country_id") setCountrySearch(newInputValue);
                          else if (f.name === "state_id") setStateSearch(newInputValue);
                          else if (f.name === "city_id") setCitySearch(newInputValue);
                        }}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            size="small"
                            placeholder={`Select ${f.label}`}
                            error={!!errors[f.name]}
                            helperText={errors[f.name]}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                height: "38px",
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      /* Standard text / date / email inputs */
                      <>
                        <Form.Control
                          type={f.type || "text"}
                          name={f.name}
                          value={form[f.name]}
                          onChange={(e) => {
                            if (f.name === "first_name" || f.name === "last_name") {
                              e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                            }
                            if (f.name === "pin_code") {
                              e.target.value = e.target.value.replace(/\D/g, "");
                            }
                            handleChange(e);
                          }}
                          readOnly={f.readOnly}
                          placeholder={`Enter ${f.label}`}
                          max={f.name === "dob" ? maxDobString : undefined}
                          style={{
                            borderColor: errors[f.name] ? "red" : undefined,
                          }}
                        />
                        {errors[f.name] && (
                          <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                            {errors[f.name]}
                          </div>
                        )}
                      </>
                    )}
                  </Form.Group>
                </Col>
              ))}
            </Row>

            {/* ── Terms & Privacy ── */}
            {signupTerms && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  Terms & Conditions Preview
                </Typography>
                <Box
                  sx={{
                    maxHeight: 150,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "#f9f9f9",
                    fontSize: "0.75rem",
                    "& p": { mb: 1 },
                    "& ul, & ol": { pl: 2, mb: 1 }
                  }}
                  dangerouslySetInnerHTML={{ __html: signupTerms }}
                />
              </Box>
            )}

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                label={
                  <>
                    I agree to{" "}
                    <span
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "#92AF5D",
                        fontWeight: "bold",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPolicyModal("signup_terms_and_condition", "Terms and Conditions");
                      }}
                    >
                      Terms and Conditions
                    </span>{" "}
                    and{" "}
                    <span
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "#FFC107",
                        fontWeight: "bold",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPolicyModal("privacy_policy", "Privacy Policy");
                      }}
                    >
                      Privacy Policy
                    </span>
                  </>
                }
              />
            </Form.Group>

            {/* ── Submit ── */}
            <Button
              className="w-100 py-2"
              style={{
                backgroundColor: "#FEB821",
                borderColor: "#fff",
                fontWeight: 600,
                opacity: !agree || loading ? 0.6 : 1,
                cursor: !agree || loading ? "not-allowed" : "pointer",
              }}
              disabled={!agree || loading}
              onClick={handleSubmit}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : (
                "Submit"
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ── Terms / Privacy Policy Modal ── */}
      <Modal
        show={showPolicyModal}
        onHide={() => setShowPolicyModal(false)}
        centered
        size="lg"
        backdrop="static"
        style={{ zIndex: 9999, marginTop: "40px" }}
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
        >
          <Typography variant="h6" fontWeight="600">
            {policyContent.title}
          </Typography>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto", padding: "24px" }}>
          {policyContent.loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : (
            <Box dangerouslySetInnerHTML={{ __html: policyContent.content }} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowPolicyModal(false)}
            style={{
              backgroundColor: "#FEB821",
              border: "none",
              color: "#000",
              fontWeight: "600",
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}